import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { authHelper } from '../utils/authHelper';

export function PaymentSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    
    if (!sessionId) {
      setStatus('error');
      return;
    }

    pollPaymentStatus(sessionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, currentAttempt = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000; // 2 seconds

    if (currentAttempt >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/status/${sessionId}`, {
        headers: authHelper.getAuthHeaders(),
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to check payment status');
      }

      const data = await response.json();
      setPaymentData(data);

      if (data.payment_status === 'paid') {
        setStatus('success');
        return;
      } else if (data.status === 'expired') {
        setStatus('expired');
        return;
      }

      // Continue polling
      setAttempts(currentAttempt + 1);
      setTimeout(() => pollPaymentStatus(sessionId, currentAttempt + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
    }
  };

  const renderContent = () => {
    switch (status) {
      case 'checking':
        return (
          <>
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#C5A14C]/20 flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-[#C5A14C] animate-spin" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-2">Verificando Pago...</h2>
            <p className="text-white/60">Por favor espera mientras confirmamos tu pago</p>
            <p className="text-sm text-white/40 mt-2">Intento {attempts + 1} de 5</p>
          </>
        );

      case 'success':
        return (
          <>
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-500/20 flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-emerald-500" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-2">¡Pago Exitoso!</h2>
            <p className="text-white/60 mb-6">Tu pago ha sido procesado correctamente</p>
            
            {paymentData && (
              <div className="bg-white/5 rounded-lg p-4 mb-6 text-left">
                <p className="text-sm text-white/40 mb-1">Monto:</p>
                <p className="text-xl font-bold text-white mb-3">
                  ${(paymentData.amount_total / 100).toFixed(2)} {paymentData.currency.toUpperCase()}
                </p>
                <p className="text-sm text-white/40 mb-1">ID de Sesión:</p>
                <p className="text-xs text-white/60 font-mono">{paymentData.session_id}</p>
              </div>
            )}

            <button
              onClick={() => navigate('/dashboard')}
              className="w-full bg-[#C5A14C] text-[#02040A] font-bold rounded-full px-8 py-4 hover:bg-[#B08F3C] transition-all duration-300"
            >
              Ir al Dashboard
            </button>
          </>
        );

      case 'expired':
      case 'timeout':
      case 'error':
        return (
          <>
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center">
              <span className="text-3xl">❌</span>
            </div>
            <h2 className="text-3xl font-bold text-white mb-2">Error en el Pago</h2>
            <p className="text-white/60 mb-6">
              {status === 'expired' && 'La sesión de pago ha expirado'}
              {status === 'timeout' && 'No pudimos verificar el estado del pago'}
              {status === 'error' && 'Ocurrió un error al verificar el pago'}
            </p>

            <button
              onClick={() => navigate('/')}
              className="w-full bg-white/10 text-white font-bold rounded-full px-8 py-4 hover:bg-white/20 transition-all duration-300"
            >
              Volver al Inicio
            </button>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#02040A] flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full bg-gradient-to-b from-white/10 to-transparent backdrop-blur-xl border border-white/20 rounded-3xl p-8 shadow-2xl text-center"
      >
        {renderContent()}
      </motion.div>
    </div>
  );
}
